/**
 * booking-address-autocomplete.js
 * 免费地址补全(Photon / Komoot —— 基于 OpenStreetMap)。
 * 用于预订表单"上车地点""下车地点"两个 input。
 *
 * 设计取舍：
 * - 无 API key，访客浏览器直连 photon.komoot.io（CORS 已开放）。
 * - 限定日本：bbox=122,24,146,46（覆盖冲绳~北海道本土）。
 * - 返回的是 OSM 原始地名(通常为日文)，无法按页面语言切换地名语言
 *   —— 这是免费方案的固有限制；如需多语言地名请改用 Google Places。
 * - 任何网络/解析失败一律静默，交还原生手输，绝不阻断订单提交。
 * - 不改 input.name；2026-10-14 字段 name 随表单英文化(出行日期等→英文，
 *   见 booking-fields.liquid)，本文件仅同步上方/下方地点两个选择器。
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 280;
  var MIN_QUERY = 2;
  var LIMIT = 8;
  var TIMEOUT_MS = 5000;
  var JP_BBOX = '122,24,146,46';
  var ENDPOINT = 'https://photon.komoot.io/api/';

  var SELECTOR =
    '[data-booking-fields] input[name="properties[Pickup Location]"],' +
    '[data-booking-fields] input[name="properties[Drop-off Location]"],' +
    '[data-address-autocomplete]';

  // 4 语言占位符（仅在 input 自身没有 placeholder 时回填）
  var PH = {
    ja: '空港・駅・ホテル・住所を検索',
    en: 'Search airport / station / hotel / address',
    'zh-tw': '搜尋機場/車站/飯店/地址',
    'zh-cn': '搜索机场/车站/酒店/地址'
  };

  // lucide 风格 stroke 图标（currentColor 自适应）
  var ICONS = {
    airport:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>',
    train:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="3" width="16" height="16" rx="2"/><path d="M4 11h16"/><path d="m5 19-2 2"/><path d="m19 19 2 2"/><circle cx="8.5" cy="15" r=".6" fill="currentColor"/><circle cx="15.5" cy="15" r=".6" fill="currentColor"/></svg>',
    hotel:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/><path d="M9 7h.01M15 7h.01M9 11h.01M15 11h.01"/></svg>',
    star:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
    pin:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>'
  };

  // 简中/繁中 → 日文/片假名 转换表
  // 原因：OSM 日本地名是日文，简中"东京"≠日文"東京"、"机场"≠"空港"，直接查命中不了
  var CN2JP = {
    '机场': '空港', '機場': '空港', '车站': '駅', '車站': '駅',
    '火车站': '駅', '火車站': '駅', '地铁站': '駅', '地铁': '地下鉄',
    '酒店': 'ホテル', '饭店': 'ホテル', '飯店': 'ホテル',
    '迪士尼': 'ディズニー', '迪斯尼': 'ディズニー', '乐园': 'ランド',
    '海洋馆': '水族館', '水族馆': '水族館', '新干线': '新幹線',
    '航站楼': 'ターミナル', '航站樓': 'ターミナル', '公园': '公園',
    '购物': 'ショッピング', '景点': '観光',
    '东': '東', '关': '関', '岛': '島', '广': '広', '长': '長',
    '冈': '岡', '冲': '沖', '绳': '縄', '涩': '渋', '叶': '葉',
    '场': '場', '丰': '豊', '滨': '浜', '机': '機', '车': '車',
    '银': '銀', '桥': '橋', '铁': '鉄', '线': '線', '电': '電',
    '馆': '館', '园': '園', '乐': '楽', '业': '業', '营': '営',
    '产': '産', '际': '際', '会': '會', '览': '覽', '饭': '飯',
    '霸': '覇', '厅': '庁', '站': '駅', '龙': '龍', '县': '縣',
    '气': '気', '济': '済', '齐': '斉', '乌': '烏'
  };

  // 把查询里的简中/繁中词替换成 Photon 能匹配的日文形式（多字词优先）
  function toJapaneseQuery(q) {
    var keys = Object.keys(CN2JP).sort(function (a, b) { return b.length - a.length; });
    var r = q;
    for (var i = 0; i < keys.length; i++) {
      if (r.indexOf(keys[i]) === -1) continue;
      r = r.split(keys[i]).join(CN2JP[keys[i]]);
    }
    return r;
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function pickIcon(key, value) {
    if (key === 'aeroway' || value === 'aerodrome') return 'airport';
    if (key === 'railway') return 'train';
    if (value === 'hotel' || /hotel|guest_house|hostel|motel/.test(value || '')) return 'hotel';
    if (key === 'tourism') return 'star';
    return 'pin';
  }

  // 把一条 Photon feature 拍平为主标题 + 副标题 + 写回值
  function format(p) {
    var main = p.name
      || [p.housenumber, p.street].filter(Boolean).join(' ')
      || p.street
      || p.locality
      || '';
    var parts = [];
    [p.locality, p.district, p.city, p.state, p.country].forEach(function (v) {
      if (v && parts.indexOf(v) === -1 && v !== main) parts.push(v);
    });
    var sub = parts.join(', ');
    return {
      main: main,
      sub: sub,
      raw: main + (sub ? ' · ' + sub : ''),
      icon: pickIcon(p.osm_key, p.osm_value)
    };
  }

  // Photon 对同一查询会返回大量重复(同站不同出口/线路)，按 raw 去重
  function dedupe(features) {
    var seen = {}, out = [];
    features.forEach(function (f) {
      var p = (f && f.properties) || {};
      // 只保留日本(Photon 的 bbox 只是偏向，会漏进非日本结果，客户端强过滤)
      if (p.countrycode !== 'JP') return;
      var info = format(p);
      if (!info.main || seen[info.raw]) return;
      seen[info.raw] = 1;
      out.push(info);
    });
    return out;
  }

  // 带超时的 fetch(避免接口卡死一直挂着)，返回 Promise<json>
  function fetchJson(url) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var to = setTimeout(function () {
        settled = true;
        reject(new Error('timeout'));
      }, TIMEOUT_MS);
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (settled) return;
          if (!r.ok) throw new Error('http ' + r.status);
          return r.json();
        })
        .then(function (d) {
          if (settled) return;
          clearTimeout(to);
          settled = true;
          resolve(d);
        })
        .catch(function (e) {
          if (settled) return;
          clearTimeout(to);
          settled = true;
          reject(e);
        });
    });
  }

  function placeholderFor() {
    var lang = String(document.documentElement.lang || '').toLowerCase();
    if (PH[lang]) return PH[lang];
    var base = lang.split('-')[0];
    return PH[base] || PH['en'];
  }

  function attach(input) {
    if (!input || input.dataset.baBound === '1') return;
    input.dataset.baBound = '1';

    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    if (!input.getAttribute('placeholder')) {
      input.setAttribute('placeholder', placeholderFor());
    }

    // 锚定到 .bf-field，下拉 absolute 定位其内
    var field = input.closest('.bf-field') || input.parentNode;
    if (getComputedStyle(field).position === 'static') {
      field.style.position = 'relative';
    }

    var box = document.createElement('div');
    box.className = 'ba-dropdown';
    box.setAttribute('role', 'listbox');
    box.hidden = true;
    field.appendChild(box);

    var rows = [];
    var active = -1;
    var seq = 0;

    function close() {
      box.innerHTML = '';
      rows = [];
      active = -1;
      box.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    }

    function render(list) {
      close();
      if (!list.length) return;
      list.forEach(function (info, i) {
        var row = document.createElement('div');
        row.className = 'ba-item';
        row.setAttribute('role', 'option');
        row.innerHTML =
          '<span class="ba-item__icon">' + (ICONS[info.icon] || ICONS.pin) + '</span>' +
          '<span class="ba-item__txt">' +
            '<span class="ba-item__main">' + escapeHtml(info.main) + '</span>' +
            (info.sub ? '<span class="ba-item__sub">' + escapeHtml(info.sub) + '</span>' : '') +
          '</span>';
        // mousedown + preventDefault 让点击在 blur 之前生效，避免列表被先清掉
        row.addEventListener('mousedown', function (e) {
          e.preventDefault();
          choose(info.raw);
        });
        box.appendChild(row);
        rows.push({ el: row, raw: info.raw });
      });
      box.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function choose(raw) {
      input.value = raw;
      input.dispatchEvent(new CustomEvent('address:select', { bubbles: true, detail: { value: raw } }));
      close();
    }

    function setActive(i) {
      if (!rows.length) return;
      rows.forEach(function (r, idx) {
        var on = idx === i;
        r.el.classList.toggle('is-active', on);
        if (on) r.el.scrollIntoView({ block: 'nearest' });
      });
      active = i;
    }

    var run = debounce(function (q) {
      var my = ++seq;
      var jq = toJapaneseQuery(q);
      var url = ENDPOINT + '?q=' + encodeURIComponent(jq) + '&limit=' + LIMIT + '&bbox=' + JP_BBOX;
      fetchJson(url)
        .then(function (d) {
          if (my !== seq) return; // 已有更新的查询，丢弃这条过期响应
          render(dedupe((d && d.features) || []));
        })
        .catch(function () { /* 静默：保持手输 */ });
    }, DEBOUNCE_MS);

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (q.length < MIN_QUERY) { close(); return; }
      run(q);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        if (!rows.length) return;
        e.preventDefault();
        setActive((active + 1) % rows.length);
      } else if (e.key === 'ArrowUp') {
        if (!rows.length) return;
        e.preventDefault();
        setActive((active - 1 + rows.length) % rows.length);
      } else if (e.key === 'Enter') {
        if (active >= 0 && rows[active]) {
          e.preventDefault();
          choose(rows[active].raw);
        }
      } else if (e.key === 'Escape') {
        if (!box.hidden) { e.preventDefault(); close(); }
      }
    });

    // blur 稍作延迟，给 mousedown 选择留出窗口；失焦即关
    input.addEventListener('blur', function () { setTimeout(close, 130); });
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // 暴露给外部，便于动态渲染的表单再次绑定
  window.TOUSHIN = window.TOUSHIN || {};
  window.TOUSHIN.attachAddressAutocomplete = attach;
})();
